import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { FaSave, FaSyncAlt, FaUserShield, FaLock, FaUnlock } from "react-icons/fa";
import { MdSecurity } from "react-icons/md";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { collection, doc, getDocs, getFirestore, orderBy, query, serverTimestamp, setDoc, writeBatch, where } from "firebase/firestore";

const db = getFirestore(firebaseApp);

const GRADIENT = "linear-gradient(135deg, #4c1d95 0%, #7c3aed 60%, #a78bfa 100%)";
const PURPLE700 = "#6d28d9";
const PURPLE100 = "#ede9fe";
const ACCENT = "#f59e0b";

const ROLE_OPTIONS_FALLBACK = [
  { code: "admin", label: "admin" },
  { code: "rh", label: "RH" },
  { code: "supervisor", label: "supervisor" },
  { code: "analista", label: "analista" },
  { code: "asistente", label: "asistente" },
  { code: "seguridad", label: "seguridad" },
  { code: "medico", label: "medico" },
  { code: "almacenero", label: "almacenero" },
  { code: "capacitador", label: "capacitador" },
  { code: "ti", label: "TI" },
  { code: "planner", label: "planner" },
  { code: "admin_nomina", label: "nomina" },
  { code: "chofer", label: "chofer" },
  { code: "user", label: "user" },
];
const NON_ASSIGNABLE_ROLES = new Set(["superAdmin"]);
const rolePermissionsCol = (tenantId) => collection(db, `governance/${tenantId}/rolePermissions`);
const ensurePayrollRoleOption = (options = []) => {
  const hasPayroll = options.some((r) => String(r.code || "").trim().toLowerCase() === "admin_nomina");
  if (hasPayroll) return options;
  return [...options, { code: "admin_nomina", label: "nomina" }];
};
const ensurePlannerRoleOption = (options = []) => {
  const hasPlanner = options.some((r) => String(r.code || "").trim().toLowerCase() === "planner");
  if (hasPlanner) return options;
  return [...options, { code: "planner", label: "planner" }];
};
const ensureChoferRoleOption = (options = []) => {
  const hasChofer = options.some((r) => String(r.code || "").trim().toLowerCase() === "chofer");
  if (hasChofer) return options;
  return [...options, { code: "chofer", label: "chofer" }];
};
const sortRoleOptionsAlpha = (options = []) =>
  [...options].sort((a, b) => String(a.label || a.code || "").localeCompare(String(b.label || b.code || ""), "es", { sensitivity: "base" }));

const MODULES = [
  { id: "usuarios", label: "Usuarios", procesos: ["USUARIOS", "USUARIOS_EDITAR", "USUARIOS_DESACTIVAR", "USUARIOS_ELIMINAR_MASIVO", "RESET_SEGURIDAD", "USUARIOS_MASIVOS", "USUARIOS_CON_ROLES", "GRUPOS", "GRUPOS_MASIVOS", "FIRMAS", "CECOS", "CECOS_MASIVOS", "ALERTAS", "CUMPLEANOS"] },
  { id: "documentos", label: "Documentos", procesos: ["DOCUMENTOS", "DESCARGAR_DOCUMENTOS_DOCUMENTAL", "CONTRATOS_GENERADOR", "CONTRATOS_PLANTILLAS", "MASIVOS", "ELIMINAR_MASIVO", "RENOMBRAR", "DUPLICAR_RUC", "FORMULARIOS", "COMUNICADOS", "REGLAMENTOS", "CORREO_MASIVOS", "GESTION_DOCS", "GESTION_FIRMAS", "PLANTILLA_FIRMAS", "PLANTILLAS", "DPLANTILLAS", "FIRMA", "BIBLIOTECA"] },
  { id: "planillas", label: "Planillas", procesos: ["BOLETAS", "BONOS", "ADELANTOS", "FERIADOS", "EMPLEADOS", "EMPLEADOS_MASIVOS", "PAGOS", "LISTA_PLANILLAS", "OCURRENCIAS", "PLANILLAS", "CONFIGURACION", "CONFIG_REGIMEN", "CONFIG_CONTABLE", "CUENTAS_CONTABLES", "EXPORTACION", "NUEVA_OCURRENCIA", "REGIMEN"] },
  { id: "asistencia", label: "Asistencia", procesos: ["APROBACIONES", "REPORTE_PDF", "COORDENADAS", "CONFIGURACION", "REGULARIZAR", "ROL_TURNOS", "EVENTOS", "REPORTES", "INGRESOS", "TRANSPORTE", "TRAZABILIDAD", "IMPORTAR", "OCURRENCIAS", "REGIMEN_TRABAJO"] },
  { id: "ausencias", label: "Ausencias", procesos: ["DASHBOARD", "ALERTAS", "IMPACTO", "DETALLE_AUSENCIAS", "AUSENTES_MES", "INDICADORES", "VACACIONES_DETALLE", "VACACIONES_MASIVAS", "ADMIN", "CONTROL_PAPELETAS", "CALENDARIO", "SOLICITUD", "INCREMENTO"] },
  { id: "epps", label: "EPPs", procesos: ["CATALOGO_EPPS", "CATALOGO_EQUIPOS", "CATALOGO_HERRAMIENTAS", "ESTADISTICA", "ASIGNAR", "PRESTAMOS"] },
  { id: "recruiting", label: "Reclutamiento", procesos: ["CANDIDATOS", "CATALOGOS", "UPLOAD_CV", "CREAR_VACANTE", "GESTION_VACANTES"] },
  { id: "onboarding", label: "Onboarding", procesos: ["DOCUMENTOS", "CONFIG_DOCS", "ESTADISTICAS", "LISTA_MAESTRA", "SEGUIMIENTO", "DESCARGAR_DOCUMENTOS_ONBOARDING", "DJ", "SOLICITUD", "MEDICO_OCUPACIONAL", "FICHA_PERSONAL"] },
  { id: "agentes", label: "Agentes AI", procesos: ["AXIS", "LEXI", "HIRO", "NOVA", "LEXI_CHAT", "LEXI_LEGAL", "HIRO_CHAT", "HIRO_CV", "ECHO_CHAT", "PROCESADOR", "VEXA"] },
  { id: "capacitaciones", label: "Capacitaciones", procesos: ["DASHBOARD", "CURSOS", "USUARIOS", "ADMINISTRADOR", "CREAR_CURSO"] },
  { id: "evaluaciones", label: "Evaluaciones", procesos: ["DASHBOARD", "CAMPANAS", "RESULTADOS", "CREAR_CAMPANA"] },
  { id: "atencion", label: "Atención", procesos: ["HELP_DESK", "RECLAMACIONES"] },
  { id: "contabilidad", label: "Contabilidad", procesos: ["COMPRAS", "FACTURAS", "PAGOS", "COBROS", "TESORERIA", "VENTAS", "PLANILLAS", "PCGE", "LIBROS", "REPORTES", "DIARIO", "MAYOR", "BALANCE-COMPROBACION", "ESTADOS-FINANCIEROS", "ANALISIS-CONTABLE", "CIERRES"] },
  { id: "compras", label: "Compras", procesos: ["ORDENES", "PROVEEDORES", "CATALOGO", "CERTIFICAR"] },
  { id: "ventas", label: "Ventas", procesos: ["ORDENES", "COMPROBANTES"] },
  { id: "tesoreria", label: "Tesorería", procesos: ["DASHBOARD", "PAGOS", "COBROS", "DIAS_CALLE", "CONCILIACION"] },
];

const PROCESS_LABELS = {
  usuarios: {
    USUARIOS: "Lista de Usuarios",
    USUARIOS_EDITAR: "Editar Usuario",
    USUARIOS_DESACTIVAR: "Desactivar Usuario",
    USUARIOS_ELIMINAR_MASIVO: "Eliminar Usuarios Masivo",
    RESET_SEGURIDAD: "Reset de Seguridad",
    USUARIOS_MASIVOS: "Usuarios Masivos",
    USUARIOS_CON_ROLES: "Usuarios con Roles",
    GRUPOS: "Grupos de Usuarios",
    GRUPOS_MASIVOS: "Carga Masiva de Grupos",
    FIRMAS: "Usuarios con Firma",
    CECOS: "Centros de Costo",
    CECOS_MASIVOS: "Asignación Masiva CECO",
    ALERTAS: "Alertas de Vencimientos",
    CUMPLEANOS: "Cumpleanos",
  },
  documentos: {
    DOCUMENTOS: "Documentos",
    DESCARGAR_DOCUMENTOS_DOCUMENTAL: "Descargar Documentos",
    CONTRATOS_GENERADOR: "Generador Contratos",
    CONTRATOS_PLANTILLAS: "Contratos Plantillas",
    MASIVOS: "Documentos Masivos",
    ELIMINAR_MASIVO: "Eliminar Documentos Masivos",
    RENOMBRAR: "Renombrar Boletas",
    DUPLICAR_RUC: "Duplicar por RUC",
    FORMULARIOS: "Formularios",
    COMUNICADOS: "Comunicados",
    REGLAMENTOS: "Reglamentos",
    CORREO_MASIVOS: "Envío Correo Masivo",
    GESTION_DOCS: "Gestión Interna de Documentos",
    GESTION_FIRMAS: "Gestión de Firmas Digitales",
    PLANTILLA_FIRMAS: "Plantilla de firmas",
    PLANTILLAS: "Plantillas del Sistema",
    DPLANTILLAS: "Plantillas Dinámicas",
    FIRMA: "Firma de Documentos",
    BIBLIOTECA: "Biblioteca",
  },
  planillas: {
    BOLETAS: "Boletas de Pago",
    BONOS: "Bonos",
    ADELANTOS: "Adelantos de Salario",
    FERIADOS: "Feriados",
    EMPLEADOS: "Empleados",
    EMPLEADOS_MASIVOS: "Empleados Masivos",
    PAGOS: "Pagos",
    LISTA_PLANILLAS: "Lista de Planillas",
    OCURRENCIAS: "Gestión de Ocurrencias",
    PLANILLAS: "Lista de Planillas / Procesar",
    CONFIGURACION: "Configuración",
    CONFIG_REGIMEN: "Regimen Laboral",
    REGIMEN: "Regimen Laboral",
    CONFIG_CONTABLE: "Configuración Contable",
    CUENTAS_CONTABLES: "Plan de Cuentas Contable",
    EXPORTACION: "Config PDT PLAME",
    NUEVA_OCURRENCIA: "Carga Ocurrencias Mes",
  },
  asistencia: {
    APROBACIONES: "Aprobaciones de Asistencia",
    REPORTE_PDF: "Reportes de Asistencia PDF",
    COORDENADAS: "Registro de Coordenadas",
    CONFIGURACION: "Asistencia",
    REGULARIZAR: "Regularización de Marcaciones",
    ROL_TURNOS: "Rol de Turnos",
    EVENTOS: "Eventos",
    REPORTES: "Asistencia",
    INGRESOS: "Servicios",
    TRANSPORTE: "Buses y Rutas",
    TRAZABILIDAD: "Trazabilidad GPS",
    IMPORTAR: "Importar Marcación Externa",
    OCURRENCIAS: "Ocurrencias",
    REGIMEN_TRABAJO: "Regimen de Trabajo",
  },
  ausencias: {
    DASHBOARD: "Dashboard Ejecutivo",
    ALERTAS: "Alertas de Vacaciones",
    IMPACTO: "Impacto Financiero",
    DETALLE_AUSENCIAS: "Ausencias",
    AUSENTES_MES: "del Mes",
    INDICADORES: "Ausentismo",
    VACACIONES_DETALLE: "Detalle de Vacaciones",
    VACACIONES_MASIVAS: "Vacaciones Masivas",
    ADMIN: "Bandeja Admin",
    CONTROL_PAPELETAS: "Control de Papeletas",
    CALENDARIO: "Calendario",
    SOLICITUD: "Nueva Solicitud",
    INCREMENTO: "Incremento Automático",
  },
  epps: {
    CATALOGO_EPPS: "Catálogo EPPs",
    CATALOGO_EQUIPOS: "Catálogo Equipos",
    CATALOGO_HERRAMIENTAS: "Catálogo Herramientas",
    ESTADISTICA: "Indicadores EPPs",
    ASIGNAR: "Entrega de Activos",
    PRESTAMOS: "Préstamos y Devoluciones",
  },
  recruiting: {
    CANDIDATOS: "Banco de Candidatos",
    CATALOGOS: "Catálogos",
    UPLOAD_CV: "CVs",
    CREAR_VACANTE: "Crear Vacante",
    GESTION_VACANTES: "Gestión de Vacantes",
  },
  onboarding: {
    DOCUMENTOS: "Documentos Onboarding",
    CONFIG_DOCS: "Documentos",
    ESTADISTICAS: "Estadísticas",
    LISTA_MAESTRA: "Lista Maestra",
    SEGUIMIENTO: "Seguimiento Onboarding",
    DESCARGAR_DOCUMENTOS_ONBOARDING: "Descargar Documentos",
    DJ: "DJ",
    SOLICITUD: "Solicitud",
    MEDICO_OCUPACIONAL: "Enviar Exámenes Médicos",
    FICHA_PERSONAL: "Ficha Personal RRHH",
  },
  agentes: {
    AXIS: "Axis - Orquestador",
    LEXI: "Lexi - Asistente Legal",
    HIRO: "Hiro - Reclutamiento",
    NOVA: "Nova - Onboarding",
    LEXI_CHAT: "Chat Principal Lexi",
    LEXI_LEGAL: "Búsqueda Legal Lexi",
    HIRO_CHAT: "Hiro - Chat",
    HIRO_CV: "CVs",
    ECHO_CHAT: "Echo - Comunicaciones",
    PROCESADOR: "Documentos",
    VEXA: "Cotizaciones",
  },
  capacitaciones: {
    DASHBOARD: "Administrar Cursos",
    CURSOS: "Visualizar Cursos Activos",
    USUARIOS: "Usuarios",
    ADMINISTRADOR: "Configuración Cursos",
    CREAR_CURSO: "Crear Curso",
  },
  evaluaciones: {
    DASHBOARD: "Dashboard",
    CAMPANAS: "Visualizar Campañas",
    RESULTADOS: "Ver Resultados de Evaluación",
    CREAR_CAMPANA: "Crear Campaña",
  },
  atencion: {
    HELP_DESK: "Mesa de Ayuda",
    RECLAMACIONES: "Libro de Reclamaciones",
  },
  contabilidad: {
    COMPRAS: "Registro de Facturas Compras",
    FACTURAS: "Gestión de Compras/Gastos",
    PAGOS: "Cuentas por Pagar",
    COBROS: "Cuentas por Cobrar",
    TESORERIA: "Integración Caja y Bancos",
    VENTAS: "Emisión de Comprobantes Ventas",
    PLANILLAS: "Integración Contable Nóminas",
    PCGE: "Plan de Cuentas (PCGE)",
    LIBROS: "Libros Electrónicos (PLE)",
    REPORTES: "Reportes Financieros",
    DIARIO: "Libro Diario",
    MAYOR: "Libro Mayor",
    "BALANCE-COMPROBACION": "Balance de Comprobación",
    "ESTADOS-FINANCIEROS": "Estados Financieros",
    "ANALISIS-CONTABLE": "Análisis Contable",
    CIERRES: "Cierres Contables",
  },
  compras: {
    ORDENES: "Órdenes de Compra",
    PROVEEDORES: "Proveedores",
    CATALOGO: "Catálogo de Ítems",
    CERTIFICAR: "Certificar / Aprobar Orden de Compra",
  },
  ventas: {
    ORDENES: "Órdenes de Venta",
    COMPROBANTES: "Emisión Comprobantes de Venta",
  },
  tesoreria: {
    DASHBOARD: "Resumen",
    PAGOS: "Pagos",
    COBROS: "Cobros",
    DIAS_CALLE: "Días en la Calle",
    CONCILIACION: "Conciliación",
  },
};

const getProcessLabel = (moduleId, processId) => {
  const mapped = PROCESS_LABELS?.[moduleId]?.[processId];
  if (mapped) return mapped;
  return String(processId || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

function PermisosPorRol({ usuario }) {
  const [roleOptions, setRoleOptions] = useState(ROLE_OPTIONS_FALLBACK);
  const [selectedRole, setSelectedRole] = useState("");
  const [templatePerms, setTemplatePerms] = useState({});
  const [adminTemplatePerms, setAdminTemplatePerms] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const canManage = useMemo(() => ["admin", "superAdmin"].includes(String(usuario?.rol || "")), [usuario]);
  const isSuperAdmin = String(usuario?.rol || "") === "superAdmin";
  const tenantId = String(usuario?.ruc || "").trim();

  const loadRoleOptions = async () => {
    try {
      let snap = await getDocs(query(collection(db, `governance/${tenantId}/rolesCatalog`), orderBy("order", "asc")));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, "rolesCatalog"), orderBy("order", "asc")));
      }
      if (snap.empty) return;
      const list = snap.docs.map((d) => { const data = d.data() || {}; return { code: String(data.code || d.id || "").trim(), label: String(data.label || data.code || d.id || "").trim() }; }).filter((r) => r.code);
      const filtered = (list.length ? list : ROLE_OPTIONS_FALLBACK).filter((r) => !NON_ASSIGNABLE_ROLES.has(String(r.code || "").trim()));
      setRoleOptions(sortRoleOptionsAlpha(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(filtered)))));
    } catch (e) { console.error(e); }
  };

  const loadRoleTemplate = async (roleCode) => {
    if (!roleCode) return;
    if (!tenantId) return;
    setLoading(true);
    try {
      const byId = await getDocs(query(rolePermissionsCol(tenantId), where("__name__", "==", roleCode)));
      setTemplatePerms(!byId.empty ? (byId.docs[0].data()?.permisos || {}) : {});
    } catch (e) { setTemplatePerms({}); }
    finally { setLoading(false); }
  };

  const loadAdminTemplate = async () => {
    if (!tenantId) return;
    try {
      const byId = await getDocs(query(rolePermissionsCol(tenantId), where("__name__", "==", "admin")));
      setAdminTemplatePerms(!byId.empty ? (byId.docs[0].data()?.permisos || {}) : {});
    } catch (e) {
      setAdminTemplatePerms({});
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    loadRoleOptions();
    loadAdminTemplate();
  }, [tenantId]); // eslint-disable-line
  useEffect(() => { if (selectedRole) loadRoleTemplate(selectedRole); }, [selectedRole, tenantId]);

  const setModuleAll = (moduleId, checked) => setTemplatePerms((prev) => ({ ...prev, [moduleId]: checked || false }));

  const toggleProcess = (moduleId, processId, checked) => {
    setTemplatePerms((prev) => {
      const current = prev[moduleId];
      const arr = Array.isArray(current) ? current : [];
      const next = checked ? Array.from(new Set([...arr, processId])) : arr.filter((p) => p !== processId);
      return { ...prev, [moduleId]: next.length ? next : false };
    });
  };

  const saveTemplate = async () => {
    if (!canManage || !selectedRole) return;
    if (!tenantId) { Swal.fire("Error", "No se detectó empresa (RUC) para guardar plantilla.", "error"); return; }
    if (selectedRole === "admin" && !isSuperAdmin) {
      Swal.fire("Acceso restringido", "Solo un superAdmin puede generar o actualizar la plantilla del rol admin.", "warning");
      return;
    }
    setSaving(true);
    try {
      await setDoc(
        doc(db, `governance/${tenantId}/rolePermissions`, selectedRole),
        {
          tenantId,
          role: selectedRole,
          permisos: templatePerms,
          updatedByUid: usuario?.uid || "",
          updatedByEmail: usuario?.email || "",
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
      if (selectedRole === "admin") {
        setAdminTemplatePerms(templatePerms || {});
      }
      Swal.fire("Guardado", `Plantilla del rol "${selectedRole}" actualizada.`, "success");
    } catch (e) { Swal.fire("Error", "No se pudo guardar la plantilla.", "error"); }
    finally { setSaving(false); }
  };

  const syncRole = async () => {
    if (!canManage || !selectedRole) return;
    if (!tenantId) { Swal.fire("Error", "No se detectó empresa (RUC) para sincronizar.", "error"); return; }
    const confirm = await Swal.fire({ title: "Sincronizar permisos", html: `Se aplicará la plantilla de <strong>${selectedRole}</strong> a todos sus usuarios.`, icon: "warning", showCancelButton: true, confirmButtonText: "Sí, sincronizar", cancelButtonText: "Cancelar" });
    if (!confirm.isConfirmed) return;
    setSyncing(true);
    try {
      const usersSnap = await getDocs(query(collection(db, "users"), where("rucEmpresa", "==", tenantId), where("rol", "==", selectedRole)));
      if (usersSnap.empty) { Swal.fire("Sin usuarios", `No hay usuarios con rol "${selectedRole}".`, "info"); setSyncing(false); return; }
      const docs = usersSnap.docs; let processed = 0;
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db);
        docs.slice(i, i + 400).forEach((d) => batch.update(d.ref, { permisos: templatePerms, updated_at: serverTimestamp() }));
        await batch.commit(); processed += Math.min(400, docs.length - i);
      }
      Swal.fire("Completado", `${processed} usuario(s) actualizados.`, "success");
    } catch (e) { Swal.fire("Error", "No se pudo completar la sincronización.", "error"); }
    finally { setSyncing(false); }
  };

  const activeModules = MODULES.filter((m) => templatePerms[m.id] !== undefined && templatePerms[m.id] !== false).length;
  const allowedModuleIdsByAdmin = new Set(
    MODULES
      .filter((m) => {
        const val = adminTemplatePerms[m.id];
        return val === true || (Array.isArray(val) && val.length > 0);
      })
      .map((m) => m.id)
  );
  const visibleModules = selectedRole === "admin"
    ? MODULES
    : MODULES.filter((m) => allowedModuleIdsByAdmin.has(m.id));

  return (
    <div className="min-vh-100" style={{ background: "#f5f3ff", padding: "24px 0" }}>
      <style>{`
        .ppr-module:hover{box-shadow:0 6px 20px rgba(109,40,217,.12)!important;transform:translateY(-1px)}
        .ppr-chip .form-check-input{position:absolute;opacity:0;width:0;height:0;pointer-events:none}
        .ppr-chip .form-check-label{display:inline-flex;align-items:center;gap:5px;background:#f3f0ff;border:1.5px solid #ddd6fe;border-radius:8px;padding:5px 12px;cursor:pointer;transition:all .15s;font-size:.82rem;font-weight:500;color:#4b5563;user-select:none;white-space:nowrap}
        .ppr-chip .form-check-input:checked + .form-check-label{background:linear-gradient(90deg,#ede9fe,#ddd6fe);border-color:#7c3aed;color:#4c1d95;font-weight:700}
        .ppr-chip .form-check-label:hover{border-color:#7c3aed;background:#ede9fe}
        .ppr-chip .form-check-input:disabled + .form-check-label{cursor:not-allowed;opacity:.55}
        .ppr-check-icon{font-size:.7rem;color:#7c3aed;font-weight:900}
        .ppr-btn{transition:all .2s}
        .ppr-btn:hover:not(:disabled){transform:scale(1.03);box-shadow:0 4px 14px rgba(109,40,217,.25)}
      `}</style>

      <Container fluid style={{ width: "94%", maxWidth: 1500 }}>
        <Card style={{ borderRadius: 15, overflow: "hidden", boxShadow: "0 8px 32px rgba(109,40,217,.1)", border: "none" }}>

          {/* Header */}
          <Card.Header className="text-white py-4 position-relative" style={{ background: GRADIENT, borderBottom: `3px solid ${ACCENT}`, borderRadius: "15px 15px 0 0" }}>
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center w-100">
              <h2 className="fw-bold mb-1">
                <FaUserShield className="me-3" size={26} />
                Permisos por Rol
              </h2>
              <p className="mb-0 opacity-75">
                Define qué puede hacer cada rol y sincroniza permisos a sus usuarios
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ background: "#faf9ff" }}>
            {!canManage && (
              <Alert className="mb-4 d-flex align-items-center gap-2" style={{ borderRadius: 12, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" }}>
                <FaLock color="#d97706" /><span>Solo <strong>Admin/SuperAdmin</strong> pueden gestionar permisos por rol.</span>
              </Alert>
            )}

            {/* Selector de rol + acciones */}
            <Card className="border-0 mb-4" style={{ background: "linear-gradient(135deg,#ede9fe,#faf5ff)", borderRadius: 14, boxShadow: "0 2px 12px rgba(109,40,217,.07)" }}>
              <Card.Body className="p-4">
                <Row className="g-3 align-items-end">
                  <Col md={5}>
                    <Form.Label className="fw-bold mb-1" style={{ color: PURPLE700, fontSize: ".9rem", textTransform: "uppercase", letterSpacing: ".04em" }}>Seleccionar Rol</Form.Label>
                    <Form.Select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      disabled={!canManage}
                      style={{ borderRadius: 10, borderColor: "#c4b5fd", fontSize: "1rem", fontWeight: 600, color: "#4c1d95", background: "#fff", padding: "10px 14px" }}
                    >
                      <option value="">— Seleccione un rol —</option>
                      {roleOptions.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={7} className="d-flex justify-content-md-end gap-2 flex-wrap">
                    <Button className="ppr-btn" onClick={saveTemplate} disabled={!canManage || !selectedRole || saving}
                      style={{ background: "linear-gradient(135deg,#ede9fe,#c4b5fd)", border: "1.5px solid #a78bfa", color: PURPLE700, fontWeight: 700, borderRadius: 10, padding: "10px 20px" }}>
                      <FaSave className="me-2" />{saving ? "Guardando..." : "Guardar plantilla"}
                    </Button>
                    <Button className="ppr-btn" onClick={syncRole} disabled={!canManage || !selectedRole || syncing}
                      style={{ background: GRADIENT, border: "none", color: "#fff", fontWeight: 700, borderRadius: 10, padding: "10px 20px" }}>
                      <FaSyncAlt className="me-2" />{syncing ? "Sincronizando..." : "Sincronizar usuarios"}
                    </Button>
                  </Col>
                </Row>
                {selectedRole === "admin" && !isSuperAdmin && (
                  <Alert className="mt-3 mb-0" style={{ borderRadius: 10, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" }}>
                    Solo un <strong>superAdmin</strong> puede generar o actualizar la plantilla del rol <strong>admin</strong>.
                  </Alert>
                )}
              </Card.Body>
            </Card>

            {/* Loading */}
            {selectedRole && loading && (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#7c3aed", width: "3rem", height: "3rem" }} />
                <p className="mt-3 fw-semibold" style={{ color: PURPLE700 }}>Cargando permisos del rol...</p>
              </div>
            )}

            {/* Empty state */}
            {!selectedRole && (
              <div className="text-center py-5">
                <div style={{ background: PURPLE100, borderRadius: 50, width: 80, height: 80, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <FaUserShield size={34} color={PURPLE700} />
                </div>
                <h5 className="fw-bold" style={{ color: "#4c1d95" }}>Selecciona un rol para configurar sus permisos</h5>
                <p className="text-muted">Elige un rol del selector superior para ver y editar su plantilla de permisos</p>
              </div>
            )}

            {/* Módulos */}
            {selectedRole && !loading && (
              <div className="d-flex flex-column gap-3">
                {selectedRole !== "admin" && visibleModules.length === 0 && (
                  <Alert style={{ borderRadius: 12, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" }}>
                    No hay módulos habilitados en la plantilla del rol <strong>admin</strong> para esta empresa.
                    Primero configura y guarda la plantilla de <strong>admin</strong>.
                  </Alert>
                )}
                {visibleModules.map((m) => {
                  const val = templatePerms[m.id];
                  const full = val === true;
                  const activeProcesses = Array.isArray(val) ? val.length : 0;
                  return (
                    <Card key={m.id} className="ppr-module border-0" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(109,40,217,.06)", transition: "all .2s", border: full ? `2px solid ${PURPLE700}` : "1.5px solid #ede9fe" }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ background: full ? "linear-gradient(135deg,#7c3aed,#a78bfa)" : PURPLE100, borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {full ? <FaUnlock color="#fff" size={16} /> : <FaLock color={PURPLE700} size={14} />}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold" style={{ color: "#1e1b4b", fontSize: "1rem" }}>{m.label}</h6>
                              <small style={{ color: "#6b7280", fontSize: ".78rem" }}>
                                {full ? "Acceso total" : activeProcesses > 0 ? `${activeProcesses} proceso(s) habilitado(s)` : "Sin acceso"}
                              </small>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ background: full ? "linear-gradient(90deg,#7c3aed,#a78bfa)" : PURPLE100, color: full ? "#fff" : PURPLE700, borderRadius: 50, padding: "3px 12px", fontSize: ".78rem", fontWeight: 600 }}>
                              {m.id}
                            </span>
                            <Form.Check
                              type="switch"
                              id={`full-${m.id}`}
                              label={<span style={{ fontSize: ".85rem", fontWeight: 600, color: PURPLE700 }}>Acceso total</span>}
                              checked={full}
                              onChange={(e) => setModuleAll(m.id, e.target.checked)}
                              style={{ "--bs-form-switch-color": "#7c3aed" }}
                            />
                          </div>
                        </div>

                        {!full && (
                          <>
                            <hr style={{ borderColor: "#ede9fe", margin: "0 0 14px" }} />
                            <div className="d-flex flex-wrap gap-2">
                              {m.procesos.map((p) => {
                                const checked = Array.isArray(val) && val.includes(p);
                                return (
                                  <div key={`${m.id}-${p}`} className="ppr-chip form-check">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`chk-${m.id}-${p}`}
                                      checked={checked}
                                      onChange={(e) => toggleProcess(m.id, p, e.target.checked)}
                                      disabled={!canManage}
                                    />
                                    <label className="form-check-label" htmlFor={`chk-${m.id}-${p}`}>
                                      {checked && <span className="ppr-check-icon me-1">✓</span>}
                                      {getProcessLabel(m.id, p)}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default PermisosPorRol;
